import { Popover, Transition } from '@headlessui/react';
import {
  graphql,
  Link,
  useStaticQuery,
} from 'gatsby';
import React, { Fragment, useEffect, useState } from 'react';
import { LocationProps } from '@models/navigation';
import { ModalType } from '@models/pages';
import { getAlertsModalButtonSlug, QUERY_IDS } from '@services';
import Hamburger from '../../../images/newhamburger.svg';
import SearchIcon from '../../../images/search-icon.svg';
import { GlobalContext } from '../../GlobalContext';
import LinkChd from '../../LinkChd';
import LivePushDown from '../../LivePushDown';
import MobileMegaMenu from '../megaMenu/MobileMegaMenu';
import SocialMediaIcons from '../navbar/SocialMediaIcons';

type Props = {
  location: LocationProps,
  lang: string,
  locales: any,
}

function setNavbarButtonStyles(id:string) {
  switch (id) {
    case (QUERY_IDS.MILLION_MATCH_QUERY_ID):
      return 'million-match-button';
    case (QUERY_IDS.BECOME_INSIDER_ID):
      return 'become-insider-button';
    default:
      return 'border-2 first-of-type:bg-chdBlue first:hover:bg-chdBlue-dark hover:bg-chdBlue first:hover:border-chdBlue-dark hover:text-white';
  }
}

export default function Header({
  location, lang, locales,
}: Props) {
  const query = useStaticQuery(graphql`
    {
      contentfulNavbar(queryId: {eq: "chd-tv-navbar"}) {
        mainNavigation {
          navigationMenuItems {
            ... on ContentfulExternalLink {
              id
              title
              internal {
                type
              }
              url
            }
            ... on ContentfulSlug {
              id
              title
              internal {
                type
              }
              slug
            }
            ... on ContentfulMegaMenu {
              id
              title
              internal {
                type
              }
              exploreTitle
              exploreLinks {
                ... on ContentfulExternalLink {
                  id
                  title
                  internal {
                    type
                  }
                  url
                }
                ... on ContentfulSlug {
                  id
                  title
                  internal {
                    type
                  }
                  slug
                }
              }
              highlightedSections {
                id
                title
                titleOrLink {
                  ... on ContentfulExternalLink {
                    id
                    title
                    internal {
                      type
                    }
                    url
                  }
                  ... on ContentfulSlug {
                    id
                    title
                    internal {
                      type
                    }
                    slug
                  }
                }
                subLinks {
                  ... on ContentfulExternalLink {
                    id
                    title
                    internal {
                      type
                    }
                    url
                  }
                  ... on ContentfulSlug {
                    id
                    title
                    internal {
                      type
                    }
                    slug
                  }
                }
              }
            }
          }
        }
        socialMediaNavigationList {
          navigationMenuItems {
            ... on ContentfulExternalLink  {
              id
              url
              title
              icon {
                title
                url
                description
              }
            }
          }
        }
      }
      contentfulHeader(queryId: {eq: "chd-tv-header"}) {
        node_locale
        headerImage {
          ... on ContentfulExternalLink {
              url
              internal {
                type
              }
              icon {
                title
                url
              }
            }
          ... on ContentfulSlug {
            slug
            icon {
              url
              description
            }
            internal {
              type
            }
          }
        }
        navigationSubmenu {
          navigationMenuItems {
            ... on ContentfulExternalLink {
              id
              queryId
              internal {
                type
              }
              title
              url
              icon {
                title
                url
              }
            }
            ... on ContentfulSlug {
              id
              queryId
              slug
              title
              internal {
                type
              }
              icon {
                title
                url
              }
            }
          }
        }
        navbarButtons {
          ... on ContentfulExternalLink {
            id
            queryId
            title
            internal {
              type
            }
            url
            icon {
              title
              url
            }
          }
          ... on ContentfulSlug {
            id
            queryId
            title
            internal {
              type
            }
            slug
          }
        }
      }
      chdSearchLink: contentfulExternalLink(
        queryId: {eq: "chd-search-link"}
      ) {
        node_locale
        title
        url
        internal {
          type
        }
      }
      chdHomeLogoLink: contentfulExternalLink(
        queryId: {eq: "childrens-health-defense-text-logo"}
      ) {
        url
        icon {
          title
          url
        }
      }
    }
  `);
  const [livePushDownActive, setLivePushDownActive] = useState(false);
  const {
    chdHomeLogoLink, chdSearchLink, contentfulNavbar, contentfulHeader,
  } = query;

  const navMenu = contentfulNavbar.mainNavigation.navigationMenuItems;
  const chdtvMenu = contentfulHeader.navigationSubmenu.navigationMenuItems ?? null;
  const navbarButtons = contentfulHeader.navbarButtons ?? null;
  const headerImage = contentfulHeader.headerImage ?? null;
  const socialMediaIcons = contentfulNavbar.socialMediaNavigationList.navigationMenuItems;
  const currentSlug = locales.find((item: any) => item.lang === lang)?.slug;

  const isDocument = typeof document !== 'undefined';
  const setNoScroll = (open: boolean) => {
    if (isDocument) {
      document.body.style.overflow = open ? 'hidden' : '';
    }
  };

  const globalContext = React.useContext(GlobalContext);
  const liveItems = globalContext?.liveItems;
  const extendedPushDown = globalContext?.extendedPushDown;
  const liveSlugs = liveItems?.map((item: any) => item.slug);
  const currentPathname = location.pathname;
  const currentPageLive = liveSlugs?.find((item: any) => item === currentPathname);
  const stylesNavMenuItems = 'text-black text-[14px] px-2.5 font-semibold leading-[26px]';
  useEffect(() => {
    if (liveItems?.length && !currentPageLive) {
      setLivePushDownActive(true);
    } else {
      setLivePushDownActive(false);
    }
  }, [globalContext]);

  const handleOpenSmsModal = () => {
    if (globalContext) {
      const { setShowModal } = globalContext;
      setShowModal(ModalType.SmsReminder);
    }
  };

  const setHamburguerMenuStyles = () => {
    if (extendedPushDown && livePushDownActive) {
      return 'left-5 top-44';
    }
    if (livePushDownActive) {
      return 'left-5 top-20';
    }
    return 'left-5 top-6 md:top-7 lg:top-10';
  };

  return (
    <div className="z-30 overflow-hidden">
      <LivePushDown lang={lang} pathname={location.pathname} />
      <div className="flex justify-center border-b-[1.5px] border-[#E7E5E2]">
        <div className="w-[1200px] flex items-center justify-between h-[68px] sm:h-[79px] px-[20px] xl:px-0">
          <Popover className="flex">
            {({ open }) => (
              <>
                {open ? setNoScroll(open) : setNoScroll(false)}
                <Popover.Button type="button" className={`flex cursor-pointer ${setHamburguerMenuStyles()}`}>
                  {!open && <img width={29} src={Hamburger} alt="Hamburger menu icon" />}
                </Popover.Button>
                {chdHomeLogoLink && (
                  <div className="hidden mt-1 ml-3 sm:block">
                    <LinkChd wrap href={chdHomeLogoLink.url}>
                      <img
                        src={chdHomeLogoLink.icon.url}
                        alt={chdHomeLogoLink.icon.description}
                        className="w-[217px]"
                      />
                    </LinkChd>
                  </div>
                )}
                <Transition
                  as={Fragment}
                  enter="transition ease-in duration-500"
                  enterFrom="translate-x-[-100%]"
                  enterTo="translate-x-0"
                  leave="transition ease-out duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-[-100%]"
                >
                  <Popover.Panel className="absolute top-0 left-0 z-50 w-full h-screen overflow-scroll bg-white lg:w-[65%]">
                    <div className="ml-5 mt-[54px]">
                      <MobileMegaMenu
                        socialMediaIcons={socialMediaIcons}
                        megaMenu={navMenu}
                        navbarButtons={navbarButtons}
                        chdSearch={chdSearchLink}
                      />
                    </div>
                  </Popover.Panel>
                </Transition>
              </>
            )}
          </Popover>
          {headerImage && (
            <div className="flex justify-start my-[20px] bg-white mr-1.5 sm:hidden">
              <Link to={headerImage.slug}>
                <img
                  src={headerImage.icon.url}
                  alt={headerImage.icon.description}
                  className="h-[40px]"
                />
              </Link>
            </div>
          )}
          <div className="flex items-center justify-end h-[50px]">
            <SocialMediaIcons
              socialMediaIcons={socialMediaIcons}
              width={26}
            />
            <div className="items-center hidden h-full ml-5 sm:flex group gap-x-5">
              {navbarButtons?.map((navButton: any) => (
                <LinkChd wrap key={navButton.id} link={navButton} linkStyles={`${setNavbarButtonStyles(navButton.queryId)} rounded-full border-chdBlue first-of-type:text-white text-black`}>
                  <button type="button" className="align-middle uppercase text-center whitespace-nowrap text-[14px] h-[42px] leading-[120%] px-[22px] py-[14px] font-bold tracking-widest">
                    {navButton.title}
                  </button>
                </LinkChd>
              ))}
            </div>
          </div>
        </div>
      </div>
      {headerImage && (
        <div className="hidden sm:flex justify-center pt-[27px] pb-[3px] w-full">
          <Link to={headerImage.slug}>
            <img
              src={headerImage.icon.url}
              alt={headerImage.icon.description}
              className="h-[65px]"
            />
          </Link>
        </div>
      )}
      <div className="flex content-center justify-center bg-white">
        <div className="flex items-center py-[17px] overflow-x-auto whitespace-nowrap scrollbar-hide">
          {chdtvMenu.map((item: any) => (!item.slug.includes(getAlertsModalButtonSlug)
            ? (
              <LinkChd
                key={item.id}
                link={item}
                linkStyles={stylesNavMenuItems}
                wrap
              >
                {item.queryId === 'chdtv-search-link' ? (<img width={14} src={SearchIcon} alt="Search icon" className="min-w-[14px]" />)
                  : item.title}
              </LinkChd>
            )
            : (
              <button
                key={item.id}
                type="button"
                className={stylesNavMenuItems}
                onClick={handleOpenSmsModal}
                disabled={item.slug === currentSlug}
              >
                {item.title}
              </button>
            )
          ))}
        </div>
      </div>
    </div>
  );
}
